/**
 * 「訂單建立中」（/orders/pending）空狀態的訂單補抓規則。
 *
 * 情境：簡訊／LINE 邀請落地時後端還沒有 platform_order，先導到 /orders/pending；
 * 之後瓦斯行在後台建單、app-api 補建 platform_order。使用者重新整理或按「重新整理」時，
 * 以訂單列表 API 找出「這次邀請之後建立的訂單」直接導過去，避免永遠卡在空狀態。
 *
 * 為了不誤導到舊的、不相關的訂單，只挑：
 *   - 建立時間 >= 進入 pending 的時間 - 10 分鐘（瓦斯行常是先建單才寄簡訊）
 *   - 有記錄 supplier_id 且列表項目帶 supplier_id 時，必須是同一間瓦斯行
 *   - 沒有記錄進入時間（storage 被清）→ 退而求其次取 24 小時內建立的訂單
 * 符合者取建立時間最新的一筆。
 */

/** 挑選所需的最小列表欄位（對齊 GET /app-api/platform-orders 的 items[]）。 */
export interface PendingOrderCandidate {
  platform_order_id: string;
  /** ISO 8601（platform_order.create_time_stamp） */
  created_at: string;
  supplier?: { supplier_id?: string | null } | null;
}

/** 進入 pending 之前多久內建立的訂單也算（瓦斯行先建單再寄簡訊）。 */
export const PENDING_LOOKBACK_MS = 10 * 60 * 1000;
/** 沒有記錄進入時間時的保底窗口。 */
export const PENDING_FALLBACK_WINDOW_MS = 24 * 60 * 60 * 1000;

export const PENDING_SINCE_KEY = "sms_invite_pending_since";
export const PENDING_SUPPLIER_KEY = "sms_invite_pending_supplier_id";

const toMs = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? null : ms;
};

/**
 * 從訂單列表挑出應該導向的訂單；沒有符合者回 null。純函式，不碰 storage。
 */
export const pickPendingOrder = <T extends PendingOrderCandidate>(
  orders: readonly T[] | null | undefined,
  opts: { pendingSince?: string | null; supplierId?: string | null; now?: number },
): T | null => {
  if (!orders?.length) return null;
  const now = opts.now ?? Date.now();
  const sinceMs = toMs(opts.pendingSince);
  const lowerBound = sinceMs !== null ? sinceMs - PENDING_LOOKBACK_MS : now - PENDING_FALLBACK_WINDOW_MS;
  const supplierId = opts.supplierId || null;

  let best: T | null = null;
  let bestMs = -Infinity;
  for (const o of orders) {
    if (!o?.platform_order_id) continue;
    const createdMs = toMs(o.created_at);
    if (createdMs === null || createdMs < lowerBound) continue;
    const itemSupplierId = o.supplier?.supplier_id;
    // 只在兩邊都有值時比對瓦斯行；列表項目沒帶 supplier 就不以此排除
    if (supplierId && itemSupplierId && itemSupplierId !== supplierId) continue;
    if (createdMs > bestMs) {
      best = o;
      bestMs = createdMs;
    }
  }
  return best;
};

/** 導到 /orders/pending 前記錄進入時間與（若有）瓦斯行；storage 不可用時靜默略過。 */
export const rememberPendingOrder = (supplierId?: string | null, now: Date = new Date()) => {
  try {
    window.localStorage.setItem(PENDING_SINCE_KEY, now.toISOString());
    if (supplierId) window.localStorage.setItem(PENDING_SUPPLIER_KEY, supplierId);
    else window.localStorage.removeItem(PENDING_SUPPLIER_KEY);
  } catch {
    // localStorage 不可用（隱私模式等）→ 讀取端會走 24 小時保底窗口
  }
};

/** 讀取進入 pending 時記錄的資訊；storage 不可用時回空值。 */
export const readPendingOrderMarker = (): { pendingSince: string | null; supplierId: string | null } => {
  try {
    return {
      pendingSince: window.localStorage.getItem(PENDING_SINCE_KEY),
      supplierId: window.localStorage.getItem(PENDING_SUPPLIER_KEY),
    };
  } catch {
    return { pendingSince: null, supplierId: null };
  }
};

/** 已導到真正訂單後清除記錄。 */
export const clearPendingOrderMarker = () => {
  try {
    window.localStorage.removeItem(PENDING_SINCE_KEY);
    window.localStorage.removeItem(PENDING_SUPPLIER_KEY);
  } catch {
    // 忽略
  }
};
